import type { Dispatch } from 'redux';

import { get, remove } from '../api';
import type { ReleaseGroup, ReleaseGroupsResponse } from '../../../shared/projectsV2/response';

export const FETCH_ALL_PROJECTS_IN_RELEASE_GROUP = 'FETCH_ALL_PROJECTS_IN_RELEASE_GROUP';
export const TOGGLE_PROJECT_GROUP_SELECT = 'TOGGLE_PROJECT_GROUP_SELECT';
export const TOGGLE_SELECT_ALL_PROJECT_GROUPS = 'TOGGLE_SELECT_ALL_PROJECT_GROUPS';
export const CLEAR_SELECTED_PROJECT_GROUPS = 'CLEAR_SELECTED_PROJECT_GROUPS';
export const UPDATE_PROJECT_GROUP = 'UPDATE_PROJECT_GROUP';
export const SET_PROJECT_GROUP_REPORT_OPTIONS = 'SET_PROJECT_GROUP_REPORT_OPTIONS';
export const SET_PROJECT_GROUP_DEPENDENCY_INFO_OPTIONS = 'SET_PROJECT_GROUP_DEPENDENCY_INFO_OPTIONS';
export const POLL_RELEASE_START = 'POLL_RELEASE_START';
export const POLL_RELEASE_STOP = 'POLL_RELEASE_STOP';
export const FETCH_ALL_PROJECT_GROUPS = 'FETCH_ALL_PROJECT_GROUPS';
export const FETCH_ONE_PROJECT_GROUP = 'FETCH_ONE_PROJECT_GROUP';
export const DELETE_PROJECT_GROUPS = 'DELETE_PROJECT_GROUPS';

//  This will use the paginated API to fetch all the release groups
export const fetchAllReleaseGroups = (filters: Record<string, unknown>) =>
  async (dispatch: Dispatch) => {
    const count = 50; // Maximum limit for a page size

    //  This inner function allows us to call it recursively to fetch all the release groups
    const fetchReleaseGroups = async (
      page: number,
      allReleaseGroups: ReleaseGroup[] = [],
    ): Promise<{ data: ReleaseGroup[] }> => {
      const { data } = await get<ReleaseGroupsResponse>(`/api/v2/release-groups`, true, {
        ...filters,
        count,
        page,
      });

      if (data) {
        const combinedReleaseGroups = [...allReleaseGroups, ...data.releaseGroups];
        //  If we've passed the total number of release groups, return the combined list.
        if (combinedReleaseGroups.length >= data.total) {
          return { data: combinedReleaseGroups };
        }
        //  Otherwise, fetch the next page of release groups.
        return fetchReleaseGroups(page + 1, combinedReleaseGroups);
      }
      return { data: allReleaseGroups };
    };

    //  Dispatch the action which kicks off fetching all of the release groups.
    return dispatch({
      type: FETCH_ALL_PROJECT_GROUPS,
      payload: async () => fetchReleaseGroups(1),
    });
  };

const actions = {
  fetchOne: (pgId: number, scanId?: number) => ({
    type: FETCH_ONE_PROJECT_GROUP,
    meta: { scanId },
    async payload() {
      return get(`/api/project_group/${pgId}`, true);
    },
  }),

  fetchAll: fetchAllReleaseGroups,
  delete: (projectGroupIDList: number[]) => ({
    type: DELETE_PROJECT_GROUPS,
    meta: {
      projectGroupIDList,
    },
    async payload() {
      const deletePromises = projectGroupIDList.map(pgId => remove(`/api/project_group/${pgId}`));
      return Promise.all(deletePromises);
    },
  }),
  selectRelease: (_, releaseId) => ({
    type: 'SELECT_RELEASE',
    payload: releaseId,
  }),
  fetchRelease: (projectGroupId, releaseId) => ({
    type: 'FETCH_PROJECT_GROUP_RELEASE',
    payload: async () => get(`/api/project_group/${projectGroupId}/release/${releaseId}`, false),
  }),
  fetchAllProjectsInReleaseGroup: (groupId: number) => ({
    type: FETCH_ALL_PROJECTS_IN_RELEASE_GROUP,
    meta: { groupId },
    payload: () => get(`/api/project_group/${groupId}/all_projects`),
  }),

  selectScan: (scanId: number | null) => ({
    type: 'SELECT_RELEASE_SCAN',
    payload: scanId,
  }),

  toggleSelect: (ids: number[]) => ({
    type: TOGGLE_PROJECT_GROUP_SELECT,
    payload: ids,
  }),

  toggleSelectAll: (isChecked: boolean) => ({
    type: TOGGLE_SELECT_ALL_PROJECT_GROUPS,
    isChecked,
  }),

  clearSelected: () => ({
    type: CLEAR_SELECTED_PROJECT_GROUPS,
  }),
  fetchReleaseSummary: (projectGroupId, releaseId) => ({
    type: 'FETCH_PROJECT_GROUP_RELEASE_SUMMARY',
    payload: () => get(`/api/project_group/${projectGroupId}/release/${releaseId}/summary`),
  }),
  fetchReleaseScans: (projectGroupId, releaseId) => ({
    type: 'FETCH_PROJECT_GROUP_RELEASE_SCANS',
    payload: () => get(`/api/project_group/${projectGroupId}/release/${releaseId}/scans`),
  }),
  scanRelease: (projectGroupId, releaseId) => ({
    type: 'SCAN_PROJECT_GROUP_RELEASE',
    payload: () => get(`/api/project_group/${projectGroupId}/release/${releaseId}/issues`),
  }),
  setReportOptions: (projectGroup, options) => ({
    type: SET_PROJECT_GROUP_REPORT_OPTIONS,
    payload: {
      projectGroup,
      options,
    },
  }),
  setDependencyInfoOptions: (projectGroup, options) => ({
    type: SET_PROJECT_GROUP_DEPENDENCY_INFO_OPTIONS,
    payload: {
      projectGroup,
      options,
    },
  }),
  pollReleaseStart: (projectGroupId, releaseId) => ({
    type: POLL_RELEASE_START,
    payload: {
      projectGroupId,
      releaseId,
    },
  }),
  pollReleaseStop: () => ({
    type: POLL_RELEASE_STOP,
  }),
};

export default actions;
